import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The provider factory — one integration point per dashboard, built the same way every time.
 *
 * Everything a dashboard needs from its host arrives through the provider this creates: where
 * the API is, how to get a token, optionally how it should look, and whatever domain-specific
 * callbacks that dashboard defines (`onOpenMission`, `onSelectProduct`, …). No component built
 * on this kit reaches outside its context — there is no import of MSAL, no router hook, no
 * global store. That is what lets the same components render inside Barnyard and inside the CNC
 * frontend without either app having to adopt the other's stack.
 *
 * The host-facing callbacks are passed through `extra` rather than as loose props so that the
 * memo dependency list is a fixed length. A rest-spread of unknown keys into a `useMemo` dep
 * array is a variable-length dep array, which React forbids and which fails on the render where
 * a host first passes an optional callback — the sort of bug that only appears in the embedding
 * application. Each dashboard's own wrapper flattens `extra` back into a friendly prop shape;
 * see `MissionTrackerProvider` for the pattern.
 */
import { createContext, useContext, useMemo } from 'react';
import { resolveTheme } from './theme.js';
export function createDashboardContext(options) {
    const Ctx = createContext(null);
    Ctx.displayName = `${options.name}Context`;
    function Provider({ children, extra, baseUrl, getToken, fetchImpl, theme, pollIntervalMs, }) {
        // `getToken` is intentionally a dependency: a host that recreates it every render would
        // otherwise pin a stale closure over its auth state, and the client would keep presenting a
        // token for a session that has ended.
        const client = useMemo(() => options.createClient({ baseUrl, getToken, fetchImpl }), [baseUrl, getToken, fetchImpl]);
        // Keyed on the serialised tokens, not on object identity. The documented integration is
        // `theme={{ accent: '#00693C' }}` written inline, which is a new object on every render — so
        // depending on identity would rebuild the theme (and, before this was split out, the API
        // client) on every keystroke in the host application.
        const themeKey = JSON.stringify(theme ?? {});
        const resolved = useMemo(() => resolveTheme(theme), [themeKey]); // eslint-disable-line react-hooks/exhaustive-deps
        const interval = pollIntervalMs ?? options.defaultPollIntervalMs ?? 30_000;
        const value = useMemo(() => ({
            client,
            theme: resolved,
            pollIntervalMs: interval,
            ...(extra ?? {}),
        }), [client, resolved, interval, extra]);
        return _jsx(Ctx.Provider, { value: value, children: children });
    }
    function useDashboard() {
        const ctx = useContext(Ctx);
        if (!ctx) {
            throw new Error(`${options.name} components must be rendered inside <${options.name}Provider>. ` +
                'Wrap your app (or just the subtree that embeds it) with it and supply ' +
                'baseUrl + getToken.');
        }
        return ctx;
    }
    return { Provider, useDashboard };
}
//# sourceMappingURL=context.js.map